/**
 * Cashflow Cockpit — API → view-model mapper.
 *
 * The recon API (GET /v1/api/recon/master) serves verbose snake_case keys
 * (invoice_number, customer_name, amount_at_stake, communication, follow_up…).
 * The components are typed against the compact shape the original report used
 * (num, cust, amt, c, fu…). This module translates one into the other so the
 * UI layer never sees the wire format. It is the single place the API contract
 * is coupled to the view model — change it here if the API shape changes.
 */

import {
	Claim,
	CockpitData,
	CommsRecord,
	Doc,
	Followup,
	Forensic,
	InvoiceRecord,
	Thread,
	ZohoMail,
	ZohoRecord,
} from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const num = (v: any): number | undefined => (v == null ? undefined : Number(v));

/** zoho{balance,invoice_date,due_date,days_overdue,last_payment_*} → ZohoRecord. */
function mapZoho(z: any): ZohoRecord {
	if (!z) return { status: 'unknown', bal: 0 };
	return {
		status: z.status,
		bal: num(z.balance) ?? 0,
		total: num(z.total),
		idate: z.invoice_date ?? null,
		due: z.due_date ?? null,
		od: num(z.days_overdue),
		paydate: z.last_payment_date ?? null,
		paymode: z.last_payment_mode ?? null,
		payamt: z.last_payment_amount ?? null,
	};
}

/** communication{email_count,n_threads,sentiment,last_contact,risk_signals,summary} → CommsRecord. */
function mapComms(c: any, coverage: any): CommsRecord {
	if (!c) return { emails: 0, threads: coverage?.n_threads ?? 0 };
	const sentiment = c.sentiment
		? [c.sentiment.overall, c.sentiment.trajectory].filter(Boolean).join(' · ')
		: null;
	const quiet =
		c.last_contact != null
			? Math.round((Date.now() - new Date(c.last_contact).getTime()) / 86400000)
			: null;
	return {
		emails: c.email_count ?? 0,
		threads: coverage?.n_threads ?? c.n_threads ?? 0,
		last: c.last_contact ?? null,
		quiet,
		ball: c.ball_in_court ?? null,
		sent: sentiment,
		risks: Array.isArray(c.risk_signals)
			? c.risk_signals.map((r: any) => (typeof r === 'string' ? r : r.type)).filter(Boolean)
			: [],
		summary: c.summary ?? null,
	};
}

/** follow_up{action,reason,recommended_recipient,suggested_next_action} → Followup. */
function mapFollowup(fu: any): Followup | undefined {
	if (!fu) return undefined;
	return {
		action: fu.action,
		send: fu.should_send,
		why: fu.reason,
		to: fu.recommended_recipient ?? null,
		next: fu.suggested_next_action ?? null,
	};
}

/** evidence.claims[] → Claim[] (the verbatim payment quotes). */
function mapClaims(evidence: any): Claim[] {
	const claims = evidence?.claims;
	if (!Array.isArray(claims)) return [];
	return claims.map((c: any) => ({
		by: c.claimed_by === 'infinitybox' ? c.account || 'InfinityBox' : c.account || c.claimed_by,
		date: c.claimed_date ?? undefined,
		paid: !!c.is_paid_claim,
		tier: c.evidence_tier,
		quote: c.quote,
		claim: c.claim_text,
		link: c.gmail_link,
	}));
}

/** threads[] → Thread[] (subject/summary/messages + per-message timeline). */
function mapThreads(threads: any): Thread[] {
	if (!Array.isArray(threads)) return [];
	return threads.map((t: any) => ({
		id: t.thread_id,
		subject: t.subject,
		sum: t.summary,
		msgs: t.messages,
		acct: t.account,
		link: t.gmail_link,
		tl: Array.isArray(t.timeline)
			? t.timeline.map((e: any) => ({ a: e.actor, d: e.date, f: e.fact, q: e.quote }))
			: [],
	}));
}

/**
 * documents[] → Doc[].
 *
 * The API attaches the same file once per email it appears on, so a thread of 20
 * replies repeats its invoice PDF / signature image 20×. Across the dataset this
 * is ~91% duplication (18k entries → ~1.5k unique files). We dedup by `s3_key`
 * (the s3_key is a content hash, so identical content collapses to one entry).
 *
 * We do NOT filter by filename: a payment screenshot is often named `image.png`,
 * the same as a logo — name-filtering would silently drop real proof. Instead we
 * carry the pipeline's `is_likely_payment_proof` / `is_likely_invoice` flags and
 * SORT by them (proofs first, then invoices, then the rest) so meaningful docs
 * surface and inline logos sink — without deleting anything.
 */
function mapDocs(documents: any): Doc[] {
	if (!Array.isArray(documents)) return [];
	const seen = new Set<string>();
	const out: Doc[] = [];
	for (const d of documents) {
		const key = d.s3_key || `${d.kind}:${d.filename}`;
		if (seen.has(key)) continue;
		seen.add(key);
		out.push({
			k: d.kind,
			f: d.filename,
			url: d.url ?? d.proof_url ?? null,
			isProof: !!d.is_likely_payment_proof,
			isInvoice: !!d.is_likely_invoice,
		});
	}
	// proofs first, then invoices, then everything else (logos/signatures sink)
	const rank = (x: Doc) => (x.isProof ? 0 : x.isInvoice ? 1 : 2);
	return out.sort((a, b) => rank(a) - rank(b));
}

/** zoho_emails[] → ZohoMail[]. */
function mapZohoMail(zm: any): ZohoMail[] {
	if (!Array.isArray(zm)) return [];
	return zm.map((m: any) => ({ date: m.date, kind: m.kind, to: m.recipients }));
}

/** forensic{summary,auditor_notes,tactics,claims,invoice_verdicts} → Forensic. */
function mapForensic(f: any): Forensic | undefined {
	if (!f) return undefined;
	return {
		summary: f.summary,
		notes: f.auditor_notes,
		tactics: Array.isArray(f.tactics)
			? f.tactics.map((t: any) => ({
					by: t.by,
					tactic: t.tactic,
					sev: t.severity,
					summary: t.summary,
					quote: t.quote,
				}))
			: [],
		claims: Array.isArray(f.claims) ? f.claims : [],
		invoice_verdicts: Array.isArray(f.invoice_verdicts) ? f.invoice_verdicts : [],
	};
}

/** A single API invoice record → the compact InvoiceRecord the UI expects. */
export function mapInvoice(r: any): InvoiceRecord {
	return {
		num: r.invoice_number,
		cust: r.customer_name,
		amt: num(r.amount_at_stake),
		labels: r.labels || [],
		tier: r.evidence?.strongest_evidence_tier ?? r.zoho_match_tier,
		refs: r.evidence?.reference_numbers ?? [],
		match: r.match_confidence,
		ostate: r.outreach_state,
		month: r.zoho?.invoice_date ? String(r.zoho.invoice_date).slice(0, 7) : undefined,
		aging: r.aging,
		kam: r.accounts?.[0],
		risk: r.communication?.sentiment?.trajectory
			? r.communication.sentiment.trajectory === 'worsening'
				? 'negative/worsening'
				: r.communication.sentiment.trajectory
			: undefined,
		vtier_inv: r.evidence?.strongest_evidence_tier,
		cpaid: r.evidence?.has_customer_paid_claim,
		ibpaid: r.evidence?.has_infinitybox_ack,
		z: mapZoho(r.zoho),
		c: mapComms(r.communication, r.coverage),
		fu: mapFollowup(r.follow_up),
		claims: mapClaims(r.evidence),
		thr: mapThreads(r.threads),
		docs: mapDocs(r.documents),
		zmail: mapZohoMail(r.zoho_emails),
		remit: Array.isArray(r.remittance) ? r.remittance : [],
		forensic: mapForensic(r.forensic),
		audit_flag: r.audit_flag,
		accts: r.accounts,
	};
}

/** Full API payload (data envelope already unwrapped) → CockpitData. */
export function mapCockpitData(apiData: any): CockpitData {
	const summary = apiData.summary || {};
	// API exposes generated_at at the top level too; ensure summary carries it
	// (the UI reads summary.generated_at).
	if (!summary.generated_at && apiData.generated_at) summary.generated_at = apiData.generated_at;
	return {
		summary,
		invoices: Array.isArray(apiData.invoices) ? apiData.invoices.map(mapInvoice) : [],
		silent: Array.isArray(apiData.silent) ? apiData.silent : [],
	};
}
